import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { getAuthHeaders, useAuth } from '../contexts/AuthContext';
import { useCountry } from '../contexts/CountryContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { LoadError, errorMessage } from '../components/LoadError';
import { StatStrip } from '../components/StatStrip';
import { canEditModule, viewOnlyHint } from '../lib/permissions';
import { platformLabel } from '../config/platforms';
import {
  AlertTriangle, Bell, BellOff, RefreshCw, Settings2, X,
  PackageX, TrendingDown, Clock, Target, Skull,
  Check, Loader2, ShieldAlert, CircleAlert, Info, RotateCcw, ShoppingCart, Megaphone, Link2 } from 'lucide-react';

import { useChannels } from '../hooks/useChannels';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_CONFIG = {
  LOW_STOCK: { icon: PackageX, label: 'Low Stock', bg: 'bg-red-50', color: 'text-red-700', dot: 'bg-red-500' },
  SALES_DROP: { icon: TrendingDown, label: 'Sales Drop', bg: 'bg-orange-50', color: 'text-orange-700', dot: 'bg-orange-500' },
  SLOW_MOVING: { icon: Clock, label: 'Slow Moving', bg: 'bg-sky-50', color: 'text-sky-700', dot: 'bg-sky-500' },
  DEAD_STOCK: { icon: Skull, label: 'Dead Stock', bg: 'bg-gray-100', color: 'text-gray-700', dot: 'bg-gray-500' },
  TARGET_AT_RISK: { icon: Target, label: 'Target at Risk', bg: 'bg-purple-50', color: 'text-purple-700', dot: 'bg-purple-500' },
  REORDER: { icon: ShoppingCart, label: 'Reorder', bg: 'bg-amber-50', color: 'text-amber-800', dot: 'bg-amber-500' },
  ADS: { icon: Megaphone, label: 'Ads', bg: 'bg-violet-50', color: 'text-violet-700', dot: 'bg-violet-500' },
  UNMATCHED_SALES: { icon: Link2, label: 'Unmapped sales', bg: 'bg-amber-50', color: 'text-amber-800', dot: 'bg-amber-500' },
  UNMATCHED_RETURNS: { icon: Link2, label: 'Unmapped returns', bg: 'bg-amber-50', color: 'text-amber-800', dot: 'bg-amber-500' },
  RETURN_SOURCE_STALE: { icon: Link2, label: 'Returns feed', bg: 'bg-red-50', color: 'text-red-800', dot: 'bg-red-500' },
};

const SEV_CONFIG = {
  CRITICAL: { label: 'Critical', bg: 'bg-red-100', text: 'text-red-700', dot: 'bg-red-500' },
  WARNING: { label: 'Warning', bg: 'bg-amber-100', text: 'text-amber-700', dot: 'bg-amber-500' },
  INFO: { label: 'Info', bg: 'bg-blue-100', text: 'text-blue-700', dot: 'bg-blue-400' },
};

const TABS = [
  { key: 'ALL', label: 'All' },
  { key: 'LOW_STOCK', label: 'Low Stock' },
  { key: 'SALES_DROP', label: 'Sales Drop' },
  { key: 'SLOW_MOVING', label: 'Slow Moving' },
  { key: 'DEAD_STOCK', label: 'Dead Stock' },
  { key: 'TARGET_AT_RISK', label: 'Target at Risk' },
  { key: 'REORDER', label: 'Reorder' },
  { key: 'ADS', label: 'Ads' },
  { key: 'UNMATCHED_SALES', label: 'Unmapped sales' },
  { key: 'UNMATCHED_RETURNS', label: 'Unmapped returns' },
  { key: 'RETURN_SOURCE_STALE', label: 'Returns feed' },
];

const THRESHOLD_FIELDS = [
  { key: 'low_stock_threshold', label: 'Low Stock', unit: 'units', min: 0, max: 100000 },
  { key: 'sales_drop_pct', label: 'Sales Drop', unit: '%', min: 1, max: 100 },
  { key: 'slow_moving_days', label: 'Slow Moving', unit: 'days', min: 1, max: 3650 },
  { key: 'dead_stock_days', label: 'Dead Stock', unit: 'days', min: 1, max: 3650 },
  { key: 'target_risk_gap_pct', label: 'Target Gap', unit: '%', min: 1, max: 100 },
];

const validateThreshold = ({ min, max, unit }, raw) => {
  const text = String(raw ?? '').trim();
  if (text === '') return 'Required';
  if (!/^\d+$/.test(text)) return 'Whole number only';
  const n = Number(text);
  if (n < min || n > max) return unit === '%' ? `Enter ${min}–${max}%` : `Enter ${min}–${max.toLocaleString('en-IN')}`;
  return null;
};

const headers = getAuthHeaders();

export const PerformanceAlertsPage = () => {
  useChannels();   // re-render when channel names or colours change (Settings → Channels)
  const { country } = useCountry();
  const { user } = useAuth();
  const canEdit = canEditModule(user, 'alerts');
  const [alerts, setAlerts] = useState([]);
  const [summary, setSummary] = useState({ total: 0, by_type: {}, by_severity: {} });
  const [settings, setSettings] = useState(null);
  // Threshold drafts are kept as raw strings so a field can be cleared while typing.
  const [draft, setDraft] = useState({});
  const [draftErrors, setDraftErrors] = useState({});
  const [savingSettings, setSavingSettings] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [filterType, setFilterType] = useState('ALL');
  const [filterSeverity, setFilterSeverity] = useState('ALL');
  const [showDismissed, setShowDismissed] = useState(false);
  const [confirmDismissAll, setConfirmDismissAll] = useState(false);


  const fetchData = useCallback(async () => {
    setRefreshing(true);
    try {
      const params = new URLSearchParams({ country });
      if (filterType !== 'ALL') params.append('alert_type', filterType);
      if (filterSeverity !== 'ALL') params.append('severity', filterSeverity);
      params.set('dismissed', showDismissed ? 'true' : 'false');

      const [alertsRes, summaryRes, settingsRes] = await Promise.all([
        axios.get(`${API}/performance-alerts?${params}`, { headers }),
        axios.get(`${API}/performance-alerts/summary?country=${country}`, { headers }),
        axios.get(`${API}/performance-alerts/settings?country=${country}`, { headers }),
      ]);
      setAlerts(alertsRes.data);
      setSummary(summaryRes.data);
      setSettings(settingsRes.data);
      setLoadError(null);
    } catch (error) {
      setLoadError(error);
      toast.error(`Failed to load alerts: ${errorMessage(error)}`);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [country, filterType, filterSeverity, showDismissed]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/performance-alerts/generate?country=${country}`, {}, { headers });
      toast.success(`Generated ${res.data.count} alerts`);
      fetchData();
    } catch {
      toast.error('Failed to generate alerts');
    } finally {
      setGenerating(false);
    }
  };

  const restoreAlerts = async (alertIds) => {
    const res = await axios.put(`${API}/performance-alerts/restore?country=${country}`, { alert_ids: alertIds }, { headers });
    fetchData();
    return { count: res.data?.count ?? alertIds.length, skipped: res.data?.skipped ?? 0 };
  };

  const handleRestore = async (alertId) => {
    try {
      const { count } = await restoreAlerts([alertId]);
      if (count > 0) toast.success('Alert restored');
      else toast.info('Not restored: a newer alert for this item is already active');
    } catch (error) {
      toast.error(`Failed to restore: ${errorMessage(error)}`);
    }
  };

  const handleDismiss = async (alert) => {
    try {
      await axios.put(`${API}/performance-alerts/${alert.alert_id}/dismiss?country=${country}`, {}, { headers });
      setAlerts(prev => prev.filter(a => a.alert_id !== alert.alert_id));
      setSummary(prev => ({
        ...prev,
        total: Math.max(0, prev.total - 1),
        by_type: { ...prev.by_type, [alert.alert_type]: Math.max(0, (prev.by_type?.[alert.alert_type] || 0) - 1) },
        by_severity: { ...prev.by_severity, [alert.severity]: Math.max(0, (prev.by_severity?.[alert.severity] || 0) - 1) },
      }));
      toast.success('Alert dismissed', {
        action: { label: 'Undo', onClick: () => handleRestore(alert.alert_id) },
      });
    } catch (error) {
      toast.error(`Failed to dismiss: ${errorMessage(error)}`);
    }
  };

  const handleDismissAll = async () => {
    try {
      const res = await axios.put(`${API}/performance-alerts/dismiss-all?country=${country}`, {}, { headers });
      const ids = res.data?.alert_ids || [];
      const count = res.data?.count ?? ids.length;
      toast.success(`Dismissed ${count.toLocaleString('en-IN')} ${count === 1 ? 'alert' : 'alerts'}`, ids.length ? {
        duration: 10000,
        action: {
          label: 'Undo',
          onClick: async () => {
            try {
              const { count: restored, skipped } = await restoreAlerts(ids);
              toast.success(`Restored ${restored.toLocaleString('en-IN')} ${restored === 1 ? 'alert' : 'alerts'}${skipped ? ` (${skipped.toLocaleString('en-IN')} skipped: newer alerts already active)` : ''}`);
            } catch (error) {
              toast.error(`Failed to undo: ${errorMessage(error)}`);
            }
          },
        },
      } : undefined);
      fetchData();
    } catch (error) {
      toast.error(`Failed to dismiss all: ${errorMessage(error)}`);
      throw error;
    }
  };

  const openSettings = () => {
    if (!showSettings && settings) {
      setDraft(Object.fromEntries(THRESHOLD_FIELDS.map(f => [f.key, settings[f.key] ?? ''])));
      setDraftErrors({});
    }
    setShowSettings(!showSettings);
  };

  const handleSaveSettings = async () => {
    const errs = {};
    THRESHOLD_FIELDS.forEach((f) => {
      const err = validateThreshold(f, draft[f.key]);
      if (err) errs[f.key] = err;
    });
    setDraftErrors(errs);
    if (Object.keys(errs).length) {
      toast.error('Fix the highlighted thresholds before saving');
      return;
    }
    const body = Object.fromEntries(THRESHOLD_FIELDS.map(f => [f.key, Number(String(draft[f.key]).trim())]));
    setSavingSettings(true);
    try {
      const res = await axios.put(`${API}/performance-alerts/settings?country=${country}`, body, { headers });
      setSettings(res.data);
      setShowSettings(false);
      toast.success('Thresholds saved. Current alerts still use the old thresholds.', {
        duration: 10000,
        action: { label: 'Regenerate now', onClick: () => handleGenerate() },
      });
    } catch (error) {
      toast.error(`Failed to save thresholds: ${errorMessage(error)}`);
    } finally {
      setSavingSettings(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="alerts-loading">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (loadError && !settings) {
    return (
      <div className="max-w-[1600px] mx-auto space-y-6" data-testid="performance-alerts-page">
        <h1 className="font-heading text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">Alerts</h1>
        <LoadError what="alerts" error={loadError} onRetry={fetchData} retrying={refreshing} testId="alerts-load-error" />
      </div>
    );
  }

  const statCards = [
    {
      key: 'CRITICAL', count: summary.by_severity?.CRITICAL || 0,
      label: 'Critical', icon: ShieldAlert,
      iconBg: 'bg-red-100', iconColor: 'text-red-600',
      accent: 'border-red-200',
    },
    {
      key: 'WARNING', count: summary.by_severity?.WARNING || 0,
      label: 'Warning', icon: CircleAlert,
      iconBg: 'bg-amber-100', iconColor: 'text-amber-600',
      accent: 'border-amber-200',
    },
    {
      key: 'INFO', count: summary.by_severity?.INFO || 0,
      label: 'Informational', icon: Info,
      iconBg: 'bg-blue-100', iconColor: 'text-blue-600',
      accent: 'border-blue-200',
    },
  ];

  return (
    <div className="max-w-[1600px] mx-auto space-y-8" data-testid="performance-alerts-page">
      {/* ── Header ── */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-heading text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl flex items-center gap-2">
            Alerts
            {refreshing && <Loader2 className="h-4 w-4 animate-spin text-gray-500" aria-label="Refreshing" />}
          </h1>
          <p className="mt-1.5 text-[13px] text-gray-500" data-testid="alerts-last-run">
            Stock, reorder, sales and target warnings in one place, rebuilt automatically every morning
            {summary.last_generated_at && ` · last updated ${new Date(summary.last_generated_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}`}
            {!canEdit && <span className="ml-1.5 text-gray-600">· View only</span>}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            onClick={openSettings}
            aria-expanded={showSettings}
            className="h-9 px-3.5 text-[13px] font-medium border-gray-200 text-gray-600 hover:bg-gray-50 rounded-lg"
            data-testid="settings-btn"
          >
            <Settings2 className="h-4 w-4 mr-1.5" /> Thresholds
          </Button>
          {canEdit && (
          <Button
            onClick={handleGenerate}
            disabled={generating}
            className="h-9 px-4 bg-gray-900 hover:bg-gray-800 text-white text-[13px] font-medium rounded-lg shadow-sm"
            data-testid="generate-alerts-btn"
          >
            {generating
              ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
              : <RefreshCw className="h-4 w-4 mr-1.5" />}
            {generating ? 'Refreshing...' : 'Refresh now'}
          </Button>
          )}
        </div>
      </div>

      {/* ── Summary: severities are filters, in one compact row ── */}
      <StatStrip testId="alerts-summary" items={statCards.map(({ key, count, label }) => ({
        key: key.toLowerCase(), label: count === 1 ? `${label.toLowerCase()} alert` : `${label.toLowerCase()} alerts`, value: count,
        tone: { CRITICAL: 'red', WARNING: 'amber', INFO: 'blue' }[key],
        active: filterSeverity === key, onClick: () => setFilterSeverity(filterSeverity === key ? 'ALL' : key),
      }))} />

      {/* ── Settings Panel ── */}
      {showSettings && settings && (
        <Card className="p-6 bg-white rounded-2xl border border-gray-100 shadow-sm" data-testid="settings-panel">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h3 className="text-base font-semibold text-gray-900">Alert Thresholds</h3>
              <p className="text-[12px] text-gray-500 mt-0.5">
                {canEdit ? 'Configure when alerts should trigger' : `View only. ${viewOnlyHint(user)}`}
              </p>
            </div>
            <Button variant="ghost" size="icon" onClick={() => setShowSettings(false)} aria-label="Close thresholds" className="h-7 w-7 text-gray-500 hover:text-gray-700" data-testid="close-settings-btn">
              <X className="h-4 w-4" />
            </Button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-5">
            {THRESHOLD_FIELDS.map((field) => {
              const { key, label, unit, min, max } = field;
              const err = draftErrors[key];
              return (
                <div key={key}>
                  <label htmlFor={`setting-${key}`} className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 block mb-1.5">
                    {label} <span className="text-gray-500 normal-case font-medium">({unit})</span>
                  </label>
                  <Input
                    id={`setting-${key}`}
                    type="number"
                    inputMode="numeric"
                    min={min}
                    max={max}
                    step={1}
                    value={draft[key] ?? ''}
                    disabled={!canEdit || savingSettings}
                    title={canEdit ? undefined : viewOnlyHint(user)}
                    aria-invalid={!!err}
                    aria-describedby={err ? `setting-${key}-error` : undefined}
                    onChange={(e) => {
                      const value = e.target.value;
                      setDraft(prev => ({ ...prev, [key]: value }));
                      if (err) setDraftErrors(prev => ({ ...prev, [key]: validateThreshold(field, value) }));
                    }}
                    onBlur={(e) => setDraftErrors(prev => ({ ...prev, [key]: validateThreshold(field, e.target.value) }))}
                    className={`h-9 text-[13px] rounded-lg ${err ? 'border-red-400 focus-visible:ring-red-300' : 'border-gray-200'}`}
                    data-testid={`setting-${key}`}
                  />
                  {err && (
                    <p id={`setting-${key}-error`} className="text-[11px] text-red-700 mt-1" data-testid={`setting-${key}-error`}>{err}</p>
                  )}
                </div>
              );
            })}
          </div>
          {canEdit && (
          <div className="mt-5 pt-4 border-t border-gray-100 flex justify-end">
            <Button onClick={handleSaveSettings} disabled={savingSettings} className="h-8 px-4 bg-gray-900 text-white text-[12px] font-medium rounded-lg" data-testid="save-settings-btn">
              {savingSettings
                ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                : <Check className="h-3.5 w-3.5 mr-1.5" />}
              {savingSettings ? 'Saving…' : 'Save Thresholds'}
            </Button>
          </div>
          )}
        </Card>
      )}

      {/* ── Control Bar ── */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        {/* Tabs */}
        <div className="flex items-center max-w-full overflow-x-auto bg-white border border-gray-200 rounded-xl p-1 shadow-sm" data-testid="filter-tabs">
          {TABS.map(({ key, label }) => {
            const count = key === 'ALL' ? summary.total : (summary.by_type?.[key] || 0);
            const active = filterType === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setFilterType(key)}
                aria-pressed={active}
                className={`px-3.5 py-1.5 rounded-lg text-[12px] font-medium transition-all duration-200 whitespace-nowrap ${
                  active
                    ? 'bg-gray-900 text-white shadow-sm'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
                data-testid={`filter-${key.toLowerCase()}`}
              >
                {label}
                {count > 0 && (
                  <span className={`ml-1.5 text-[10px] tabular-nums ${active ? 'text-gray-300' : 'text-gray-500'}`}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Right actions */}
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setShowDismissed(!showDismissed)}
            aria-pressed={showDismissed}
            className={`h-8 px-3 rounded-lg text-[12px] font-medium border transition-all duration-200 flex items-center gap-1.5 ${
              showDismissed
                ? 'bg-gray-900 text-white border-gray-900'
                : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300'
            }`}
            data-testid="toggle-dismissed"
          >
            <BellOff className="h-3.5 w-3.5" />
            {showDismissed ? 'Dismissed' : 'Show Dismissed'}
          </button>
          {canEdit && summary.total > 0 && !showDismissed && (
            <Button
              variant="outline"
              onClick={() => setConfirmDismissAll(true)}
              className="h-8 px-3 text-[12px] font-medium border-gray-200 text-gray-500 hover:text-gray-700 rounded-lg"
              data-testid="dismiss-all-btn"
            >
              Dismiss All
            </Button>
          )}
          <ConfirmDialog
            open={confirmDismissAll}
            onOpenChange={setConfirmDismissAll}
            title={`Dismiss all ${summary.total.toLocaleString('en-IN')} active ${summary.total === 1 ? 'alert' : 'alerts'}?`}
            description={`This dismisses every active alert for ${country}, including any hidden by the current type or severity filter. Dismissed alerts move to "Show Dismissed", and you can undo from the confirmation message.`}
            confirmLabel={`Dismiss ${summary.total.toLocaleString('en-IN')} ${summary.total === 1 ? 'alert' : 'alerts'}`}
            busyLabel="Dismissing…"
            destructive
            onConfirm={handleDismissAll}
            testId="dismiss-all-confirm-dialog"
          />
        </div>
      </div>

      {/* ── Alert List ── */}
      {alerts.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20" data-testid="no-alerts">
          <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
            <Bell className="h-7 w-7 text-gray-300" />
          </div>
          <p className="text-[15px] font-medium text-gray-700">
            {showDismissed ? 'No dismissed alerts' : 'No active alerts'}
          </p>
          <p className="text-[13px] text-gray-500 mt-1 max-w-xs text-center">
            {showDismissed
              ? 'Dismissed alerts will appear here'
              : canEdit
                ? 'Alerts are rebuilt automatically every morning. Click "Refresh now" to check again'
                : 'Nothing needs attention right now'}
          </p>
        </div>
      ) : (
        <div className={`grid grid-cols-1 xl:grid-cols-2 gap-4 transition-opacity ${refreshing ? 'opacity-60' : ''}`} data-testid="alerts-list" aria-live="polite" aria-busy={refreshing}>
          {alerts.map((alert) => {
            const tc = TYPE_CONFIG[alert.alert_type] || TYPE_CONFIG.LOW_STOCK;
            const sc = SEV_CONFIG[alert.severity] || SEV_CONFIG.INFO;
            const TypeIcon = tc.icon;

            return (
              <Card
                key={alert.alert_id}
                className={`bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md hover:border-gray-200 transition-all duration-200 overflow-hidden group ${alert.dismissed ? 'opacity-75' : ''}`}
                data-testid={`alert-${alert.alert_id}`}
              >
                <div className="flex">
                  {/* Status strip */}
                  <div className={`w-1 flex-shrink-0 ${tc.dot}`} />

                  <div className="flex-1 p-4">
                    {/* Header row */}
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-2.5">
                        <div className={`w-8 h-8 rounded-lg ${tc.bg} flex items-center justify-center flex-shrink-0`}>
                          <TypeIcon className={`h-4 w-4 ${tc.color}`} strokeWidth={2} />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <span className={`text-[10px] font-bold uppercase tracking-wide rounded-md px-1.5 py-0.5 ${sc.bg} ${sc.text}`}>
                              {sc.label}
                            </span>
                            <span className={`text-[10px] font-semibold uppercase tracking-wide rounded-md px-1.5 py-0.5 ${tc.bg} ${tc.color}`}>
                              {tc.label}
                            </span>
                          </div>
                          <p className="text-[13px] font-semibold text-gray-900 leading-snug">{alert.title}</p>
                        </div>
                      </div>
                      {canEdit && !alert.dismissed && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDismiss(alert)}
                          aria-label={`Dismiss alert: ${alert.title}`}
                          title="Dismiss alert"
                          className="h-7 w-7 flex-shrink-0 text-gray-500 hover:text-gray-800 opacity-100 md:opacity-0 md:group-hover:opacity-100 focus-visible:opacity-100 transition-opacity"
                          data-testid={`dismiss-${alert.alert_id}`}
                        >
                          <X className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      {canEdit && alert.dismissed && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleRestore(alert.alert_id)}
                          className="h-7 px-2 flex-shrink-0 text-[11px] text-gray-700 border-gray-200"
                          data-testid={`restore-${alert.alert_id}`}
                        >
                          <RotateCcw className="h-3 w-3 mr-1" /> Restore
                        </Button>
                      )}
                    </div>

                    {/* Body */}
                    <p className="text-[12px] text-gray-500 mt-2 leading-relaxed pl-[42px]">
                      {alert.message}
                    </p>

                    {/* Metadata footer */}
                    {(alert.sku_code || alert.platform || alert.brand || alert.category) && (
                      <div className="mt-3 pt-2.5 border-t border-gray-50 pl-[42px] flex items-center gap-2 flex-wrap">
                        {alert.sku_code && (
                          <span className="text-[10px] font-mono text-gray-600 bg-gray-50 border border-gray-100 px-2 py-0.5 rounded-md">
                            {alert.sku_code}
                          </span>
                        )}
                        {alert.platform && (
                          <span className="text-[10px] font-medium text-gray-600 bg-gray-50 border border-gray-100 px-2 py-0.5 rounded-md">
                            {platformLabel(alert.platform)}
                          </span>
                        )}
                        {alert.brand && (
                          <span className="text-[10px] text-gray-500">{alert.brand}</span>
                        )}
                        {alert.category && (
                          <span className="text-[10px] text-gray-500 before:content-['·'] before:mr-1.5">{alert.category}</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};
